import { Link, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@quietbuildlab/ui";
import { signOut } from "../lib/auth-client";
import { meQueryOptions } from "../queries/auth";
import { useEffect, useState } from "react";
import { CommandPalette } from "./command-palette";

export function SiteHeader() {
  const router = useRouter();
  const qc = useQueryClient();
  const session = router.options.context.session;
  const [palette, setPalette] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function handleSignOut() {
    await signOut();
    await qc.invalidateQueries({ queryKey: meQueryOptions.queryKey });
    await router.invalidate();
  }

  return (
    <header className="sticky top-0 z-10 backdrop-blur border-b border-[color:var(--color-line)] bg-[color:color-mix(in_oklch,var(--color-bg)_85%,transparent)]">
      <div className="mx-auto max-w-5xl px-4 sm:px-5 h-14 flex items-center justify-between gap-3">
        <Link to="/" className="font-semibold tracking-tight">
          Bargain Radar
        </Link>

        {/* Desktop search trigger — opens CommandPalette. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setPalette(true)}
          className="hidden sm:flex flex-1 max-w-md justify-start gap-2 bg-[color:var(--color-panel)] hover:bg-[color:var(--color-panel-2)] text-xs font-normal text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg-muted)]"
        >
          🔍 Search deals…
          <span className="ml-auto text-[10px]">⌘K</span>
        </Button>

        {/* Mobile search icon — same target. */}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Open search"
          onClick={() => setPalette(true)}
          className="sm:hidden text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-panel)] hover:text-[color:var(--color-fg-muted)]"
        >
          🔍
        </Button>

        <nav className="flex items-center gap-3 sm:gap-4 text-sm">
          <Link
            to="/"
            className="hidden sm:inline text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
            activeProps={{ className: "text-[color:var(--color-fg)]" }}
          >
            Hot
          </Link>
          {session ? (
            <><Link to="/alerts" className="hidden sm:inline text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]">Alerts</Link><DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="flex h-auto items-center gap-2 rounded-full border border-[color:var(--color-line)] py-1 pl-1 pr-3 text-xs font-normal hover:bg-[color:var(--color-panel)]"
                >
                  <Avatar user={session.user} />
                  <span>{session.user.name ?? session.user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 bg-[color:var(--color-panel)] border-[color:var(--color-line)]">
                <DropdownMenuItem
                  onSelect={() => void handleSignOut()}
                  className="focus:bg-[color:var(--color-panel-2)]"
                >
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu></>
          ) : (
            <Link
              to="/login"
              className="rounded-md border border-[color:var(--color-line)] px-3 py-1.5 hover:bg-[color:var(--color-panel)]"
            >
              Sign in
            </Link>
          )}
        </nav>
      </div>
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
    </header>
  );
}

function Avatar({ user }: { user: { name?: string | null; email: string; image?: string | null } }) {
  if (user.image) {
    return (
      <img
        src={user.image}
        alt=""
        width={24}
        height={24}
        className="rounded-full"
        referrerPolicy="no-referrer"
      />
    );
  }
  const initial = (user.name ?? user.email).trim().charAt(0).toUpperCase();
  return (
    <span className="grid place-items-center w-6 h-6 rounded-full bg-[color:var(--color-accent)] text-[color:var(--color-bg)] text-[10px] font-semibold">
      {initial}
    </span>
  );
}
